/* eslint-disable @typescript-eslint/no-explicit-any */

const SIGNIN_TYPE = 'mi-aem-homepage-spa/components/content/signin';
const ACCOUNT_CONTAINER_TYPE = 'mi-aem-account/components/content/accountcontainer';

/** Shared depth-first walk for AEM `:items` / nested object graphs. */
function depthFirstFind(obj: any, predicate: (node: any) => boolean): any | null {
  if (!obj || typeof obj !== 'object') return null;
  if (predicate(obj)) return obj;
  if (obj[':items']) {
    for (const key of Object.keys(obj[':items'])) {
      const found = depthFirstFind(obj[':items'][key], predicate);
      if (found) return found;
    }
  }
  for (const key of Object.keys(obj)) {
    if (key !== ':items' && typeof obj[key] === 'object') {
      const found = depthFirstFind(obj[key], predicate);
      if (found) return found;
    }
  }
  return null;
}

function findObjectByType(obj: any, targetType: string): any | null {
  return depthFirstFind(obj, n => n?.[':type'] === targetType);
}

/** First account flyout container under the header (left/right parsys). */
function findFlyoutAccountContainer(obj: any): any | null {
  return depthFirstFind(
    obj,
    n => n?.[':type'] === ACCOUNT_CONTAINER_TYPE && (n.isFlyout === true || n.isFlyout === 'true')
  );
}

/** AEM account containers use `:items` for parsys; `AccountContainer` expects `cqItems`. */
function normalizeFlyoutAccountForStore(flyoutAccount: any): any {
  if (!flyoutAccount || typeof flyoutAccount !== 'object') return null;
  const { ':items': parsysItems, ...rest } = flyoutAccount;
  return {
    ...rest,
    cqItems: parsysItems ?? flyoutAccount.cqItems,
  };
}

/**
 * When the global nav uses the account flyout (`enableAccountFlyout`), `SignInDrawer` only dispatches
 * `ACCOUNT_MODAL_OPEN_EVENT` and reads homepage sign-in fields for the trigger (resourceProperties, etc.).
 * Merge the authored flyout account subtree with the homepage sign-in trigger so one object satisfies both.
 */
function mergeFlyoutAccountWithHomepageSignInTrigger(flyoutAccount: any, homepageSignIn: any): any {
  const base = normalizeFlyoutAccountForStore(flyoutAccount);
  if (!base) return null;
  if (!homepageSignIn) return base;
  return {
    ...homepageSignIn,
    // Keep signin trigger shape intact; only graft in flyout parsys content.
    cqItems: base.cqItems ?? homepageSignIn.cqItems,
  };
}

/**
 * Extracts the sign-in drawer model from the AEM page model.
 * Prefers the global-header account flyout (`accountcontainer` with `isFlyout`), merged with the homepage
 * `signin` trigger so `SignInDrawer` labels and `NavigationUtilityLink` flyout behavior stay aligned with
 * the same authored subtree under `header-responsivegrid`.
 *
 * @param model - The full AEM page model (pageProps.model)
 * @returns The sign-in model object or null if not found
 *
 * @example
 * // In _app.tsx:
 * const signInModel = extractSignInModelFromPageModel(model);
 * if (signInModel) {
 *   setSignInDrawerModel(signInModel);
 * }
 */
export function extractSignInModelFromPageModel(model: any): any | null {
  if (!model?.cqItems?.root?.[':items']?.['header-responsivegrid']) {
    return null;
  }

  const headerGrid = model.cqItems.root[':items']['header-responsivegrid'];
  const homepageSignIn = findObjectByType(headerGrid, SIGNIN_TYPE);
  const flyoutAccount = findFlyoutAccountContainer(headerGrid);

  if (flyoutAccount) {
    return mergeFlyoutAccountWithHomepageSignInTrigger(flyoutAccount, homepageSignIn);
  }

  return homepageSignIn;
}
